/* eslint-disable */

const RADIOS = {};
const SPLITTER = /\s+/;

/**
 * Messaging system supporting both events, and request/reply, with namespacing.
 * Loosely based on Backbone Radio.
 *
 * @author wasche
 * @since Mar 2017
 *
 * Moved to web components by skline, 2019-04-02.
 */
class Radio {
  constructor(channel) {
    this.channel = channel;
    this.events = {};
    this.replies = {};
  }

  /**
   * Set up a reply for requests for a certain name.
   *
   * @param {String} name - descriptive name(s), like 'thing:property'
   * @param {Function} callback - function that will return the value
   * @param {Object=this} context - context the callback will be called in
   * @return object - this, for chaining
   */
  reply(name, callback, context) {
    name.split(SPLITTER).forEach(n => {
      this.replies[n] || (this.replies[n] = []);
      this.replies[n].push({
        callback: callback,
        context: context || this,
      });
    }, this);
    return this;
  }

  /**
   * Request a value by name.
   *
   * @param {String} name - descriptive name
   * @param args - arguments to pass to the callback
   * @return value from previously registered reply
   */
  request(name, ...args) {
    if (!this.replies[name]) {
      return null;
    }
    let r = this.replies[name];
    r = r[r.length - 1];

    return r.callback.apply(r.context, args);
  }

  /**
   * Request a response from all providers registered for it.
   *
   * @param {String} name - descriptive name
   * @param args - arguments to pass to the callback
   * @return array of values from previously registered replies, or null
   */
  requestMany(name, ...args) {
    if (!this.replies[name]) {
      return null;
    }

    return this.replies[name].map(r => r.callback.apply(r.context, args));
  }

  /**
   * Check if ALL providers return the expected response. Returns true if there
   * are no registered providers.
   *
   * @param {String} name - descriptive name
   * @param expected - value to compare, or function
   * @param args - arguments to pass to the callback
   * @return boolean - true if all providers return the expected response, false otherwise
   */
  requestAll(name, expected, ...args) {
    if (!this.replies[name]) {
      return true;
    }
    args = [expected, ...args];
    const fn = typeof expected === 'function' ? expected : v => v === expected;

    return this.replies[name].reduce((b, r) => b && fn(r.callback.apply(r.context, args)), true);
  }

  get requestEvery() {
    return this.requestAll;
  }

  /**
   * Check if ANY providers return the expected response. Returns false if there
   * are no registered providers.
   *
   * @param {String} name - descriptive name
   * @param expected - value to compare, or function
   * @param args - rest of the arguments to be passed to the callbacks
   * @return boolean - true if all providers return the expected response, false otherwise
   */
  requestAny(name, expected, ...args) {
    if (!this.replies[name]) {
      return false;
    }
    args = [expected, ...args];
    const fn = typeof expected === 'function' ? expected : v => v === expected;

    return this.replies[name].reduce((b, r) => b || fn(r.callback.apply(r.context, args)), false);
  }

  get requestSome() {
    return this.requestAny;
  }

  /**
   * Listen for an event.
   *
   * @param {String} name - event name(s)
   * @param {Function} callback - function to call when event occurs
   * @param {Object=this} context - context in which to call the callback
   * @return object - this, for chaining
   */
  on(name, callback, context) {
    name.split(SPLITTER).forEach(n => {
      this.events[n] || (this.events[n] = []);
      this.events[n].push({
        callback: callback,
        context: context || this,
      });
    });
    return this;
  }

  /**
   * Listen for an event just once.
   *
   * @param {String} name - event name(s)
   * @param {Function} callback - function to call when event occurs
   * @param {Object=this} context - context in which to call the callback
   * @return object - this, for chaining
   */
  once(name, callback, context) {
    name.split(SPLITTER).forEach(n => {
      this.events[n] || (this.events[n] = []);
      this.events[n].push({
        callback: callback,
        context: context || this,
        single: true,
      });
    });
    return this;
  }

  get one() {
    return this.once;
  }

  /**
   * Stop listening to an event. If no callback is passed, removes all callbacks
   * for the event.
   *
   * @param {String} name - event name(s)
   * @param {function} callback - listener to remove
   * @return object - this, for chaining
   */
  off(name, callback) {
    name.split(SPLITTER).forEach(n => {
      if (!this.events[n]) {
        return;
      }
      this.events[n] = this.events[n].filter(o => callback && o.callback !== callback);
    });
    return this;
  }

  /**
   * Fire an event. Any additional arguments are passed to the listeners.
   *
   * @param {String} name - event name
   * @param args - arguments to be passed to the handlers
   * @return object - this, for chaining
   */
  trigger(name, ...args) {
    if (!this.events[name]) {
      return undefined;
    }

    this.events[name] = this.events[name].filter(o => {
      o.callback.apply(o.callback, args);
      return !o.single;
    });
    return this;
  }

  get emit() {
    return this.trigger;
  }

  /**
   * Clears all events and replies
   *
   * @return object - this, for chaining
   */
  clear() {
    this.events = {};
    this.replies = {};
    return this;
  }
}

let radio = channel => RADIOS[channel] || (RADIOS[channel] = new Radio(channel));

// To work with the AMD radio which may also be loaded on the same page, we'll do
// something hacky here: we'll create a race condition where one of us will win and
// be _the_ radio for the page. If the other radio has already installed itself,
// we'll use it instead of the instance we just created. As long as it continues
// to be true that both radios implement the same interface, this is perfectly safe.
if (!SERVER) {
  if (window.require && window.require.defined && window.require.defined('common/Radio')) {
    radio = window.require('common/Radio');
  } else if (window.define) {
    window.define('common/Radio', [], () => radio);
  }
} else {
  // Don't provide a radio in server mode, just give a stub
  const _radio = {
    on: function() {},
    off: function() {},
    emit: function() {},
    request: function() {},
  };
  radio = function() {
    return _radio;
  };
}

export default radio;
